import React from 'react';
import { Button } from '../ui/button';
import { Loader2, Bell, RefreshCw, ExternalLink } from 'lucide-react';
import ListingRow from './ListingRow';

export default function ListingsDisplay({
  alert, filteredListings, scrapedListings, viewMode, tierData,
  expandedTiers, toggleTier, loadingListings, summary, getPriceColor, getPriceBadge,
  mostRecentSessionTime, handleRefresh, refreshing
}) {
  const isRunningOrRecent = alert?.status === 'running' || (alert?.status === 'error' && alert?.last_run && (new Date() - new Date(alert.last_run)) < 600000);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      {/* Running banner */}
      {isRunningOrRecent && (
        <div className={`mb-4 ${alert?.status === 'error' ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4`}>
          <div className="flex items-center gap-3">
            <Loader2 className={`w-5 h-5 ${alert?.status === 'error' ? 'text-yellow-600' : 'text-blue-600'} animate-spin`} />
            <div className="flex-1">
              <p className={`${alert?.status === 'error' ? 'text-yellow-900' : 'text-blue-900'} font-medium`}>
                {alert?.status === 'error' ? 'Partial scraping in progress...' : 'Scraping in progress...'}
              </p>
              <p className={`${alert?.status === 'error' ? 'text-yellow-700' : 'text-blue-700'} text-sm`}>
                {scrapedListings.length > 0 ? `Found ${scrapedListings.length} items so far. More results coming...`
                  : alert?.status === 'error' ? 'One marketplace had issues, but others may still be running...'
                  : 'Searching marketplaces for matching items...'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">
          All Results ({filteredListings.length})
          {isRunningOrRecent && scrapedListings.length > 0 && <span className="text-sm font-normal text-blue-600 ml-2">• Live updating</span>}
        </h2>
        {loadingListings && (
          <div className="flex items-center gap-2 text-blue-600">
            <Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Updating...</span>
          </div>
        )}
      </div>

      {filteredListings.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          {isRunningOrRecent ? (
            <><Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-2 animate-spin" /><p className="text-gray-600 font-medium">Searching for items...</p><p className="text-gray-500 text-sm mt-1">This may take 5-15 minutes</p></>
          ) : (
            <>
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-600 mb-4">{scrapedListings.length === 0 ? 'No results found yet' : 'No results match your filters'}</p>
              {scrapedListings.length === 0 && (
                <Button onClick={handleRefresh} disabled={refreshing} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />Run Scrape Now
                </Button>
              )}
            </>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['#', 'Title', 'Price', 'Seller', 'Date', 'Link'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredListings.map((listing, index) => (
                <tr key={listing._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">#{index + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{listing.title}</td>
                  <td className="px-4 py-3"><span className={`text-sm font-bold ${getPriceColor(listing.price)}`}>S${listing.price}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-600">{listing.seller_name || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(listing.scraped_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><a href={listing.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800"><ExternalLink className="w-4 h-4" /></a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-4">
          {tierData.strategy && tierData.stats && (
            <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <span className="font-medium">💡 Smart Grouping:</span> {
                tierData.strategy === 'too_few_results' ? `Showing all ${tierData.stats.count} results (too few to divide)`
                  : tierData.strategy === 'low_price_variation' ? `All results shown (prices similar: $${tierData.stats.minPrice.toFixed(0)} - $${tierData.stats.maxPrice.toFixed(0)})`
                  : tierData.strategy === 'moderate_variation' ? `Divided into 2 tiers (moderate price range: $${tierData.stats.minPrice.toFixed(0)} - $${tierData.stats.maxPrice.toFixed(0)})`
                  : `Divided into 3 tiers (wide price range: $${tierData.stats.minPrice.toFixed(0)} - $${tierData.stats.maxPrice.toFixed(0)})`
              }
            </div>
          )}
          {tierData.tiers && tierData.tiers.map((tier, index) => {
            const isExpanded = expandedTiers.hasOwnProperty(index) ? expandedTiers[index] : tier.expanded;
            return (
              <div key={index} className="border rounded-lg overflow-hidden">
                <button onClick={() => toggleTier(index)}
                  className={`w-full flex items-center justify-between p-4 ${tier.color === 'green' ? 'bg-green-50 hover:bg-green-100' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}>
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">{tier.icon}</span>
                    <h3 className={`font-bold ${tier.color === 'green' ? 'text-green-700' : 'text-gray-700'}`}>{tier.name}</h3>
                    <span className={`text-sm ${tier.color === 'green' ? 'text-green-600' : 'text-gray-600'}`}>({tier.items.length} items)</span>
                  </div>
                  <span className={`text-xl ${tier.color === 'green' ? 'text-green-600' : 'text-gray-600'}`}>{isExpanded ? '▼' : '▶'}</span>
                </button>
                {isExpanded && (
                  <div className="space-y-2 p-4 bg-white">
                    {tier.items.map((listing, listingIndex) => (
                      <ListingRow key={listing._id} listing={listing} index={listingIndex} summary={summary} getPriceColor={getPriceColor} getPriceBadge={getPriceBadge} mostRecentSessionTime={mostRecentSessionTime} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
